import { useState, useEffect } from 'react'
import { Search, Layers } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { adminSectionLibraryService } from '../../services/admin.sectionLibrary.service'
import type { SectionLibraryDocument } from '@/types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (libraryId: string) => Promise<void>
}

export function LibrarySectionPicker({ open, onOpenChange, onSelect }: Props) {
  const [sections, setSections] = useState<Array<SectionLibraryDocument & { id: string }>>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [adding, setAdding] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setLoading(true)
    adminSectionLibraryService
      .getAll()
      .then(setSections)
      .finally(() => setLoading(false))
  }, [open])

  const filtered = sections.filter(
    (s) =>
      s.title.toLowerCase().includes(search.toLowerCase()) ||
      s.sectionId.toLowerCase().includes(search.toLowerCase()) ||
      (s.category ?? '').toLowerCase().includes(search.toLowerCase())
  )

  async function handleSelect(id: string) {
    setAdding(id)
    try {
      await onSelect(id)
      onOpenChange(false)
    } finally {
      setAdding(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Section from Library</DialogTitle>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search sections…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="max-h-96 space-y-2 overflow-y-auto">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)
          ) : filtered.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {sections.length === 0 ? 'Section library is empty.' : 'No matching sections.'}
            </p>
          ) : (
            filtered.map((section) => {
              const fieldCount = section.fields?.length ?? 0
              return (
                <button
                  key={section.id}
                  type="button"
                  disabled={adding === section.id}
                  onClick={() => void handleSelect(section.id)}
                  className="flex w-full items-center gap-3 rounded-md border p-3 text-left transition-colors hover:bg-accent disabled:opacity-50"
                >
                  <Layers className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{section.title}</p>
                    <p className="font-mono text-xs text-muted-foreground">{section.sectionId}</p>
                  </div>
                  <Badge variant="outline" className="shrink-0 text-xs">
                    {fieldCount} {fieldCount === 1 ? 'field' : 'fields'}
                  </Badge>
                  {section.category && (
                    <Badge variant="secondary" className="shrink-0 text-xs">
                      {section.category}
                    </Badge>
                  )}
                </button>
              )
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
